import { api } from "./api";
import type { Paginated, PageSize } from "./pagination";

export type CanonicalStatus = "draft" | "active" | "deprecated";

export interface CanonicalSummary {
  canonical_type: string;
  version: number;
  status: CanonicalStatus;
  description: string | null;
  /** Free-form organizational tags. Editable on any status via PATCH /tags. */
  tags: string[];
  created_by: string;
  created_at: string;
  activated_at: string | null;
  deprecated_at: string | null;
}

export interface CanonicalDetail extends CanonicalSummary {
  yaml_definition: string;
  compiled_schema: Record<string, unknown>;
}

export interface ValidateResponse {
  valid: boolean;
  errors: string[];
  canonical_type?: string;
  version?: number;
  compiled_schema?: Record<string, unknown>;
}

export interface TestPayloadResponse {
  valid: boolean;
  errors: string[];
  normalized?: Record<string, unknown>;
}

const base = "/canonical";

export const canonicalApi = {
  list: (params?: { page?: number; page_size?: PageSize; tag?: string[] }) =>
    api.get<Paginated<CanonicalSummary>>(base, { query: params }),
  get: (type: string, version: number) =>
    api.get<CanonicalDetail>(`${base}/${type}/v${version}`),
  validate: (yaml: string) =>
    api.post<ValidateResponse>(`${base}/validate`, { yaml_definition: yaml }),
  create: (yaml: string, tags?: string[]) =>
    api.post<CanonicalDetail>(base, { yaml_definition: yaml, tags: tags ?? [] }),
  updateDraft: (type: string, version: number, yaml: string) =>
    api.put<CanonicalDetail>(`${base}/${type}/v${version}`, {
      yaml_definition: yaml,
    }),
  /** Replace the tag list. Allowed on draft / active / deprecated. */
  updateTags: (type: string, version: number, tags: string[]) =>
    api.patch<CanonicalDetail>(`${base}/${type}/v${version}/tags`, { tags }),
  activate: (type: string, version: number) =>
    api.post<CanonicalDetail>(`${base}/${type}/v${version}/activate`),
  deprecate: (type: string, version: number) =>
    api.post<CanonicalDetail>(`${base}/${type}/v${version}/deprecate`),
  remove: (type: string, version: number) =>
    api.delete<void>(`${base}/${type}/v${version}`),
  test: (type: string, version: number, payload: Record<string, unknown>) =>
    api.post<TestPayloadResponse>(`${base}/${type}/v${version}/test`, { payload }),
};
